import { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useFieldArray, useForm } from 'react-hook-form';
import { Button } from '$components/elements/button';
import { Checkbox } from '$components/elements/checkbox';
import { Headline, StyledHeadline } from '$components/elements/headline';
import { InputField } from '$components/elements/input-field';
import { Modal } from '$components/elements/modal';
import { RichText } from '$components/elements/rich-text';
import { Select } from '$components/elements/select/select';
import { Text } from '$components/elements/text';
import { Flex, Grid, VerticalGutter } from '$components/layouts';
import { brandLeg } from '$constants/env';
import { PageTypes } from '$constants/pages';
import { usePageData, useRaptor, useSWR, useTranslation } from '$hooks';
import { API_URL, formatString, formatStringWithContent } from '$lib/helpers';
import { http } from '$lib/http';
import { ClubSignupFormData, ICMSPage, ISignUpModelArgs, IStoreInformation } from '$models';
import { signUpUserToAgillic, signUpUserToBC365 } from '$services/club-signup.service';
import { ClubSignupChild, ClubSignupChildButton } from '../components/club-signup';
import { SpotLayoutWithHeading } from '../components/spot-layout-with-heading';

export type ClubSignupSpotProps = {
    header: string;
    spotHeader: string;
    text: string;
    permissionSource: string;
    chainId: string;
    type: 'agillic' | 'BC365';
    withChildren?: boolean;
};

type ExtendedFormData = {
    marketing: boolean;
} & ClubSignupFormData;

export const ClubSignupSpot: FC<ClubSignupSpotProps> = (props: ClubSignupSpotProps) => {
    const { translate, translateBrand } = useTranslation();
    const [isSignedUp, setIsSignedUp] = useState(false);
    const { addUser } = useRaptor();
    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<ExtendedFormData>();
    const {
        fields: children,
        append,
        remove,
    } = useFieldArray({
        control,
        name: 'children',
    });
    const [showLinkOneModal, setShowLinkOneModal] = useState(false);
    const [showLinkTwoModal, setShowLinkTwoModal] = useState(false);
    const [termsOnePageData, setTermsOnePageData] = useState<ICMSPage>();
    const [termsTwoPageData, setTermsTwoPageData] = useState<ICMSPage>();

    const { pageData: pageOneData } = usePageData<ICMSPage>(
        termsOnePageData?.content || [],
        termsOnePageData?.bottomContent || [],
        PageTypes.RichTextPage
    );
    const { pageData: pageTwoData } = usePageData<ICMSPage>(
        termsTwoPageData?.content || [],
        termsTwoPageData?.bottomContent || [],
        PageTypes.RichTextPage
    );

    useEffect(() => {
        fetchTermsOneData();
        fetchTermsTwoData();
    }, []);

    const { data: stores } = useSWR<IStoreInformation[]>(API_URL + '/scom/api/stores/getAllForMyPage');

    const onSubmit = handleSubmit((data) => onComplete(data));

    const onComplete = async (data: ClubSignupFormData) => {
        // Raptor add user event
        addUser(data.email);

        const result = await (props.type === 'BC365' ? signUpBC365() : signUpAgillic());

        if (result) {
            setIsSignedUp(true);
        }

        async function signUpAgillic() {
            const signUpModel: ISignUpModelArgs = {
                name: data.name,
                email: data.email,
                mobileNumber: data.phoneNumber,
                storeId: data.store,
                permissionSource: props.permissionSource,
            };

            if (props.withChildren && data.children?.length > 0) {
                signUpModel.children = data.children;
            }

            const result = await signUpUserToAgillic(signUpModel);
            return result;
        }

        async function signUpBC365() {
            const signUpModel = {
                name: data.name,
                email: data.email,
                phoneNumber: data.phoneNumber,
                storeId: data.store,
                permissionSource: props.permissionSource,
            };
            const result = await signUpUserToBC365(signUpModel);
            return result;
        }
    };

    const fetchTermsOneData = async () => {
        const termsUrl = translate('terms.membership.termsLinkOnePath');
        if (!termsUrl || termsOnePageData) {
            return;
        }
        try {
            const response = await http(`${API_URL}${termsUrl}`);
            if (response.ok) {
                const termsData = await response.json();
                setTermsOnePageData(termsData);
            }
        } catch (e) {
            //
        }
    };
    const fetchTermsTwoData = async () => {
        const termsUrl = translate('terms.membership.termsLinkTwoPath');
        if (!termsUrl || termsTwoPageData) {
            return;
        }
        try {
            const response = await http(`${API_URL}${termsUrl}`);
            if (response.ok) {
                const termsData = await response.json();
                setTermsTwoPageData(termsData);
            }
        } catch (e) {
            //
        }
    };

    return (
        <>
            <SpotLayoutWithHeading {...props} heading={props.spotHeader} subHeading={''} ignoreVisibilityClass>
                <Flex justifyContent="center">
                    <SpotContainer>
                        {!isSignedUp && (
                            <form onSubmit={onSubmit}>
                                <StyledInputField
                                    type="tel"
                                    autoComplete="tel"
                                    spacingBottom="extrasmall"
                                    label={translate('checkout.contactForm.phoneNumber')}
                                    error={errors.phoneNumber}
                                    {...register('phoneNumber', {
                                        required: translate('validation.error.phoneRequired'),
                                        pattern: {
                                            value: /^[ ()+]*([0-9][ ()+]*){8}$/,
                                            message: translate('validation.error.phoneInvalid'),
                                        },
                                    })}
                                />
                                <Grid flow="row" columns={'1fr 1fr'} columnGap="medium">
                                    <StyledInputField
                                        spacingBottom="extrasmall"
                                        autoComplete="name"
                                        label={translate('checkout.contactForm.name')}
                                        error={errors.name}
                                        {...register('name', {
                                            required: translate('validation.error.nameRequired'),
                                        })}
                                    />
                                    <StyledInputField
                                        spacingBottom="extrasmall"
                                        type="email"
                                        autoComplete="email"
                                        label={translate('checkout.contactForm.email')}
                                        error={errors.email}
                                        {...register('email', {
                                            required: translate('validation.error.emailRequired'),
                                        })}
                                    />
                                </Grid>
                                <Select
                                    label={translate('signup.storePlaceholder')}
                                    error={errors.store}
                                    {...register('store', {
                                        required: translate('signup.storeValidation'),

                                        validate: (x) =>
                                            (x && x !== translate('signup.storePlaceholder')) ||
                                            translate('signup.storeValidation'),
                                    })}
                                >
                                    {stores?.map((x) => (
                                        <option key={x.id} value={x.customerId}>
                                            {x.customerName}
                                        </option>
                                    ))}
                                </Select>
                                <Flex gap="small">
                                    <VerticalGutter>
                                        <TermsCheckbox
                                            label={
                                                <div>
                                                    <Text>
                                                        {formatStringWithContent(
                                                            translate('terms.membership.termsLabel'),
                                                            <TermsLink
                                                                variant="link"
                                                                onClick={() => setShowLinkOneModal(true)}
                                                            >
                                                                {translate('terms.membership.termsLinkOneText')}
                                                            </TermsLink>,
                                                            <TermsLink
                                                                variant="link"
                                                                onClick={() => setShowLinkTwoModal(true)}
                                                            >
                                                                {translate('terms.membership.termsLinkTwoText')}
                                                            </TermsLink>
                                                        )}
                                                    </Text>
                                                </div>
                                            }
                                            error={errors.terms}
                                            {...register('terms', {
                                                required: translate('validation.error.fieldRequired'),
                                            })}
                                        />
                                        <MarketingCheckbox
                                            label={
                                                <div>
                                                    <Text>
                                                        {formatString(
                                                            translate('terms.membership.marketingLabel'),
                                                            brandLeg
                                                                ? translate('general.brandLEG')
                                                                : translate('general.brandBOI')
                                                        )}
                                                    </Text>
                                                </div>
                                            }
                                            error={errors.marketing}
                                            {...register('marketing', {
                                                required: translate('validation.error.fieldRequired'),
                                            })}
                                        />
                                    </VerticalGutter>
                                </Flex>

                                {props.withChildren && (
                                    <>
                                        <StyledHeadline size={4}>{translate('signup.children.title')}</StyledHeadline>

                                        {children.map((child, index) => (
                                            <ClubSignupChild
                                                key={child.id}
                                                index={index}
                                                register={register}
                                                errors={errors}
                                                removeChild={(id: number) => remove(id)}
                                            />
                                        ))}

                                        <ClubSignupChildButton
                                            index={children.length}
                                            addChild={(data) => {
                                                append(data);
                                            }}
                                        />
                                    </>
                                )}
                                <Flex justifyContent="center">
                                    <Button type="submit">
                                        {translate('signup.subscribe', {
                                            query: {
                                                club: translateBrand(
                                                    'signup.clubBOI',
                                                    'signup.clubLEG',
                                                    'signup.clubBOG'
                                                ),
                                            },
                                        })}
                                    </Button>
                                </Flex>
                            </form>
                        )}
                        {isSignedUp && (
                            <Flex alignItems="center" justifyContent="center">
                                <Headline size={5}>
                                    {translate('signup.successMessage', {
                                        query: {
                                            club: translateBrand('signup.clubBOI', 'signup.clubLEG', 'signup.clubBOG'),
                                        },
                                    })}
                                </Headline>
                            </Flex>
                        )}
                    </SpotContainer>
                </Flex>
            </SpotLayoutWithHeading>
            <Modal show={showLinkOneModal} onDismiss={() => setShowLinkOneModal(false)}>
                <Headline size={3}>{pageOneData?.heading}</Headline>
                <RichText noPadding text={pageOneData?.body} />
            </Modal>
            <Modal show={showLinkTwoModal} onDismiss={() => setShowLinkTwoModal(false)}>
                <Headline size={3}>{pageTwoData?.heading}</Headline>
                <RichText noPadding text={pageTwoData?.body} />
            </Modal>
        </>
    );
};

const SpotContainer = styled.div(({ theme }) => ({
    maxWidth: theme.spots.signupFormMaxWidth,
    width: '100%',
}));

const StyledInputField = styled(InputField)({
    width: '100%',
});

const TermsCheckbox = styled(Checkbox)(({ theme }) => ({
    marginBottom: theme.space[5],
}));

const MarketingCheckbox = styled(Checkbox)(({ theme }) => ({
    marginBottom: theme.space[5],
}));

const TermsLink = styled(Button)(({ theme }) => ({
    color: theme.colors.red,
    textDecoration: 'none',
    fontSize: theme.fontSizes.xs,
    flex: 1,
    display: 'inline-flex',
}));
